import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';
import API_URL from '../../config';
import { 
    Typography, 
    TextField, 
    Button, 
    TableContainer, 
    Table, 
    TableHead, 
    TableRow, 
    TableBody, 
    TableCell, 
    IconButton, 
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Box
} from './TailwindMuiStubs';

export default function CategoryManagement() {
    const [categories, setCategories] = useState([]);
    const [marathonTypes, setMarathonTypes] = useState([]);
    const [snack, setSnack] = useState(null);
    const [loading, setLoading] = useState(false);

    // Dialog state
    const [open, setOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        marathon_type_id: '',
        crew_size_min: 1,
        crew_size_max: 2,
        rental_available: false,
        is_public: true
    });

    const fetchData = async () => {
        try {
            const [catRes, typeRes] = await Promise.all([
                fetch(`${API_URL}/categories`, { credentials: 'include' }),
                fetch(`${API_URL}/marathon-types`, { credentials: 'include' })
            ]);
            const catData = await catRes.json();
            const typeData = await typeRes.json();
            console.log("Categories data:", catData);
            console.log("Marathon types data:", typeData);
            if (Array.isArray(catData)) setCategories(catData);
            if (Array.isArray(typeData)) setMarathonTypes(typeData);
        } catch (e) {
            console.error("Fetch error", e);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const showSnack = (msg, severity = 'success') => {
        setSnack({ msg, severity });
        setTimeout(() => setSnack(null), 3000);
    };

    const handleOpen = (cat = null) => {
        if (cat) {
            setEditingCategory(cat);
            setFormData({
                name: cat.name,
                marathon_type_id: cat.marathon_type_id,
                crew_size_min: cat.crew_size_min,
                crew_size_max: cat.crew_size_max,
                rental_available: !!cat.rental_available,
                is_public: !!cat.is_public
            });
        } else {
            setEditingCategory(null);
            setFormData({
                name: '',
                marathon_type_id: marathonTypes[0]?.id || '',
                crew_size_min: 1,
                crew_size_max: 2,
                rental_available: false,
                is_public: true
            });
        }
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSubmit = async () => {
        if (!formData.name || !formData.marathon_type_id) {
            return showSnack('Vyplňte názov a typ maratónu', 'error');
        }
        
        setLoading(true);
        const url = editingCategory 
            ? `${API_URL}/categories/${editingCategory.id}` 
            : `${API_URL}/categories`;
        const method = editingCategory ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include'
            });
            
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Uloženie zlyhalo');
            }

            showSnack(editingCategory ? 'Kategória upravená' : 'Kategória vytvorená');
            handleClose();
            fetchData();
        } catch (e) {
            showSnack(e.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Naozaj vymazať túto kategóriu?")) return;
        try {
            const res = await fetch(`${API_URL}/categories/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            if (!res.ok) return showSnack(data.error || 'Chyba pri mazaní', 'error');
            showSnack('Kategória vymazaná');
            fetchData();
        } catch (e) {
            showSnack('Chyba spojenia', 'error');
        }
    };

    return (
        <div className="h-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                    <Typography variant="h4">Správa Kategórií</Typography>
                    <Typography variant="body2">
                        Definovanie typov lodí a pravidiel pre veľkosť posádky.
                    </Typography>
                </div>
                <Button 
                    startIcon={<Plus size={18}/>} 
                    onClick={() => handleOpen()}
                    className="md:w-auto w-full"
                >
                    Nová Kategória
                </Button>
            </div>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Názov</TableCell>
                            <TableCell>Typ Maratónu</TableCell>
                            <TableCell>Veľkosť posádky</TableCell>
                            <TableCell>Požičovňa</TableCell>
                            <TableCell>Verejná</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map(cat => (
                            <TableRow key={cat.id}>
                                <TableCell>{cat.id}</TableCell>
                                <TableCell>
                                    <span className="font-bold text-[#1E3A8A]">{cat.name}</span>
                                </TableCell>
                                <TableCell>
                                    {marathonTypes.find(t => t.id === cat.marathon_type_id)?.name || 'Neznámy'}
                                </TableCell>
                                <TableCell>
                                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600 font-medium">
                                        {cat.crew_size_min === cat.crew_size_max ? cat.crew_size_min : `${cat.crew_size_min} - ${cat.crew_size_max}`} os.
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span className={`w-2.5 h-2.5 rounded-full inline-block mr-2 ${cat.rental_available ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                                    {cat.rental_available ? 'Áno' : 'Nie'}
                                </TableCell>
                                <TableCell>
                                    <span className={`text-xs px-2 py-0.5 rounded ${cat.is_public ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-orange-50 text-orange-600 border border-orange-100'}`}>
                                        {cat.is_public ? 'Verejná' : 'Skrytá'}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <div className="flex gap-1">
                                        <IconButton onClick={() => handleOpen(cat)} color="primary"><Edit size={16}/></IconButton>
                                        <IconButton onClick={() => handleDelete(cat.id)} color="error"><Trash2 size={16}/></IconButton>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingCategory ? 'Upraviť kategóriu' : 'Pridať novú kategóriu'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
                        <TextField 
                            label="Názov kategórie" 
                            fullWidth 
                            value={formData.name} 
                            onChange={e => setFormData({...formData, name: e.target.value})}
                            placeholder="napr. C2 Mix"
                        />
                        
                        <FormControl fullWidth>
                            <InputLabel>Typ Maratónu</InputLabel>
                            <Select 
                                value={formData.marathon_type_id} 
                                onChange={e => setFormData({...formData, marathon_type_id: e.target.value})}
                            >
                                {marathonTypes.map(t => (
                                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <div className="grid grid-cols-2 gap-4">
                            <TextField 
                                label="Min. počet členov" 
                                type="number" 
                                value={formData.crew_size_min}
                                onChange={e => setFormData({...formData, crew_size_min: parseInt(e.target.value)})}
                            />
                            <TextField 
                                label="Max. počet členov" 
                                type="number" 
                                value={formData.crew_size_max}
                                onChange={e => setFormData({...formData, crew_size_max: parseInt(e.target.value)})}
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setFormData({...formData, rental_available: !formData.rental_available})}>
                                <Checkbox checked={formData.rental_available} />
                                <span className="text-sm text-gray-700">Dostupná lode z požičovne</span>
                            </div>
                            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setFormData({...formData, is_public: !formData.is_public})}>
                                <Checkbox checked={formData.is_public} />
                                <span className="text-sm text-gray-700">Zobraziť v registračnom formulári</span>
                            </div>
                        </div>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose} disabled={loading}>Zrušiť</Button>
                    <Button onClick={handleSubmit} disabled={loading} startIcon={loading ? <X className="animate-spin" size={18}/> : <Save size={18}/>}>
                        {loading ? 'Ukladám...' : 'Uložiť zmeny'}
                    </Button>
                </DialogActions>
            </Dialog>

            {snack && (
                <div className="fixed bottom-6 right-6 z-[9999] animate-in fade-in slide-in-from-bottom-4">
                    <Alert severity={snack.severity}>{snack.msg}</Alert>
                </div>
            )}
        </div>
    );
}
